import { useState, useCallback, useEffect, useMemo } from "react";
import "../Style/Milestone.css";
import { useLanguage } from "../Context/Languagecontext";

/* ── Icons ── */
const IconAward   = () => <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="6"/><path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12"/></svg>;
const IconNetwork = () => <svg viewBox="0 0 24 24"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>;
const IconBuilding= () => <svg viewBox="0 0 24 24"><path d="M3 21h18M9 8h1m-1 4h1m4-4h1m-1 4h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/></svg>;
const IconStar    = () => <svg viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const IconUsers   = () => <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconTrophy  = () => <svg viewBox="0 0 24 24"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/></svg>;
const IconChevR   = () => <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>;
const IconChevL   = () => <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>;
const IconStar2   = () => <svg viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;

/* ══════════════════════════════════════
    CURVED CAROUSEL 3D ANGLES
══════════════════════════════════════ */
const RADIUS      = 900;
const ANGLE_STEP  = 22;
const MAX_VISIBLE = 2;

function getTransform(offset) {
  const angle = offset * ANGLE_STEP;
  const rad   = (angle * Math.PI) / 180;
  const tx    = Math.sin(rad) * RADIUS;
  const tz    = Math.cos(rad) * RADIUS - RADIUS;
  const ry    = -angle;
  const scale = offset === 0 ? 1 : Math.max(0.72, 1 - Math.abs(offset) * 0.14);
  const opacity = offset === 0 ? 1 : Math.max(0.0, 1 - Math.abs(offset) * 0.42);
  return { tx, tz, ry, scale, opacity };
}

export default function Milestones() {
  const [active, setActive] = useState(0);
  const [, setAnimDir] = useState(0);

  const { t, lang } = useLanguage();
  const moduleData = t.milestonesModule;

  // Icon mapping helper array matching item layout IDs
  const iconsList = useMemo(() => [
    <IconAward />,
    <IconBuilding />,
    <IconStar />,
    <IconUsers />,
    <IconNetwork />,
    <IconTrophy />
  ], []);

  /* ── Structural Assembly of Dynamic Localized Content Data ── */
  const milestones = useMemo(() => {
    const rawItems = moduleData?.items || [];
    return rawItems.map((item, index) => ({
      ...item,
      icon: iconsList[index] || <IconStar />
    }));
  }, [moduleData, iconsList]);

  const total = milestones.length;

  const goTo = useCallback((idx) => {
    if (idx === active || idx < 0 || idx >= total) return;
    setAnimDir(idx > active ? 1 : -1);
    setActive(idx);
  }, [active, total]);

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev]);

  return (
    <section className={`milestones-section ${lang === 'ta' ? 'milestones-section--tamil' : ''}`} id="milestones">
      {/* ── Header ── */}
      <div className="milestones-header">
        <span className="milestones-eyebrow">{moduleData?.eyebrow}</span>
        <h2 className="milestones-title">
          {moduleData?.titlePre}<em>{moduleData?.titleEm}</em>
        </h2>
        <p className="milestones-subtitle">{moduleData?.subtitle}</p>
        <div className="milestones-divider">
          <span/><i>✦ ✦ ✦</i><span/>
        </div>
      </div>

      {/* ── Curved Carousel Stage ── */}
      <div className="carousel-stage" aria-label="Milestones carousel">
        <div className="carousel-wheel">
          {milestones.map((m, i) => {
            const offset = i - active;
            if (Math.abs(offset) > MAX_VISIBLE + 1) return null;
            const { tx, tz, ry, scale, opacity } = getTransform(offset);
            const isActive = offset === 0;
            const cls = [
              'carousel-item',
              isActive                   ? 'carousel-item--active' : '',
              Math.abs(offset) === 1     ? 'carousel-item--side'   : '',
              Math.abs(offset) > 1       ? 'carousel-item--far'    : '',
            ].filter(Boolean).join(' ');

            return (
              <div
                key={m.id}
                className={cls}
                style={{
                  transform: `translateX(${tx}px) translateZ(${tz}px) rotateY(${ry}deg) scale(${scale})`,
                  opacity,
                  zIndex: 10 - Math.abs(offset),
                  pointerEvents: isActive ? 'all' : Math.abs(offset) <= 1 ? 'all' : 'none',
                }}
                onClick={() => !isActive && goTo(i)}
                role={!isActive ? 'button' : undefined}
                aria-label={!isActive ? `Go to: ${m.contentTitle}` : undefined}
              >
                <div className="ci-inner">
                  {/* ── Left visual panel ── */}
                  <div className="ci-visual">
                    <span className="ci-num">0{m.id}</span>
                    <div>
                      <div className="ci-icon" aria-hidden="true">{m.icon}</div>
                      <p className="ci-category">{m.category}</p>
                      <h3 className="ci-visual-title">
                        {m.visualTitlePre}<em>{m.visualTitleEm}</em>{m.visualTitlePost}
                      </h3>
                    </div>
                    <div className="ci-stats">
                      <div className="ci-stat-divider"/>
                      {m.highlights && m.highlights.map((h, j) => (
                        <div key={j} className="ci-stat">
                          <span className="ci-stat-val">{h.val}</span>
                          <span className="ci-stat-lbl">{h.lbl}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Right content panel ── */}
                  <div className="ci-content">
                    <div className="ci-meta">
                      <span className="ci-tag">{m.tag}</span>
                      <span className="ci-idx">{String(i+1).padStart(2,'0')} / {String(total).padStart(2,'0')}</span>
                    </div>
                    <h4 className="ci-title">{m.contentTitle}</h4>
                    <div className="ci-body">
                      {m.badgeText && (
                        <div className="award-badge">
                          <IconStar2/> {m.badgeText}
                        </div>
                      )}
                      
                      {m.paragraphs && m.paragraphs.map((pText, pIdx) => (
                        <p key={pIdx}>{pText}</p>
                      ))}

                      {m.points && (
                        <div className="content-points">
                          {m.points.map((ptText, ptIdx) => (
                            <div key={ptIdx} className="content-point">{ptText}</div>
                          ))}
                        </div>
                      )}

                      {m.presenterLine && (
                        <p className="presenter-line">{m.presenterLine}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Progress dots ── */}
      <div className="milestones-progress" role="tablist">
        {milestones.map((_, i) => (
          <button
            key={i}
            className={`progress-dot${i===active?' active':i<active?' done':''}`}
            onClick={() => goTo(i)}
            aria-label={`Milestone ${i+1}`}
          />
        ))}
      </div>

      {/* ── Nav buttons ── */}
      <nav className="milestones-nav" aria-label="Milestone navigation">
        <button className="nav-btn secondary" onClick={prev} disabled={active===0} aria-label="Previous">
          <IconChevL/> {moduleData?.controls?.prev || "Prev"}
        </button>
        <span className="nav-counter">{active+1} / {total}</span>
        <button className="nav-btn primary" onClick={next} disabled={active===total-1} aria-label="Next">
          {active===total-1 ? (moduleData?.controls?.complete || 'Complete') : (moduleData?.controls?.next || 'Next Milestone')}
          {active<total-1 && <IconChevR/>}
        </button>
      </nav>
    </section>
  );
}